/**
 * Configuration & Constants
 * API endpoints, cache settings, and thresholds
 * Station/region data is now loaded from /data/regions.json
 */

import { getCurrentTimezone, getCurrentBuoy, AppState } from './state.js';
import { CACHE_TTL, TIMEOUTS, INTERVALS } from './time.js';
import { THRESHOLDS as RATING_THRESHOLDS } from '../shared/rating/index.mjs';

// ===========================================
// Environment Detection (single source of truth)
// ===========================================
export const isLocalhost = ['localhost', '127.0.0.1'].includes(location.hostname);
export const isProduction = !isLocalhost;

// ===========================================
// Timezone (dynamic - reads from state)
// ===========================================
// For backwards compatibility, export TIMEZONE as a getter
// New code should use getCurrentTimezone() from state.js
export function getTimezone() {
    return getCurrentTimezone();
}

// ===========================================
// Thresholds (combines rating thresholds with fetch timeouts)
// ===========================================
export const THRESHOLDS = {
    // Timeouts (milliseconds) - from shared time module
    fetch: {
        direct: TIMEOUTS.fetchDirect,
        proxy: TIMEOUTS.fetchProxy,
        text: TIMEOUTS.fetchText
    },
    // Import all rating thresholds from shared module
    ...RATING_THRESHOLDS
};

// ===========================================
// Stations (dynamic - reads from state)
// ===========================================
// Stations are now loaded from /data/regions.json
// Use getStations() for current region's stations
// or access AppState.currentRegion.stations directly
export function getStations() {
    return AppState.currentRegion?.stations || {};
}

export const CONFIG = {
    // API endpoints for Netlify Functions
    api: {
        waterLevel: '/api/water-level',
        predictions: '/api/predictions',
        curve: '/api/curve',
        buoy: '/api/buoy',
        weather: '/api/weather',
        sun: '/api/sun',
        // New: Centralized conditions endpoint (reads from Supabase)
        conditions: '/api/get-conditions'
    },
    // Fallback for local development
    noaaBaseUrl: 'https://api.tidesandcurrents.noaa.gov/api/prod/datagetter',
    weatherBaseUrl: 'https://api.weather.gov',
    // Dynamic buoy URL - use getBuoyUrl() for current buoy
    get buoyUrl() {
        const buoyId = getCurrentBuoy();
        return `https://www.ndbc.noaa.gov/data/realtime2/${buoyId}.txt`;
    },
    // CORS proxies for fallback direct API calls
    // Note: Third-party proxies can be unreliable. Production uses Netlify Functions which don't need CORS.
    // These are mainly for local development when Supabase/Netlify Functions aren't available.
    // Proxies are tried in order; if one fails, the next is attempted.
    corsProxies: [
        'https://api.allorigins.win/raw?url=',  // Generally reliable, good uptime
        'https://corsproxy.io/?',                // Fast but may have rate limits
        'https://api.codetabs.com/v1/proxy?quest=',  // Alternative option
        'https://corsproxy.org/?'                // Backup option
    ],
    refreshInterval: INTERVALS.refresh,
    // Dynamic current station - reads from state
    get currentStation() {
        return AppState.currentStationId || '9410170';
    },
    set currentStation(value) {
        // For backwards compatibility, allow setting
        // But prefer using state.setCurrentStation()
        AppState.currentStationId = value;
    }
};

// Cache configuration (TTL in milliseconds) - values from shared time module
// Keys are now base keys - region/station is added dynamically by cache.js
export const CACHE_CONFIG = {
    // Primary: All conditions from Supabase (5-min refresh matches poll-conditions.mjs)
    conditions: { key: 'conditions', ttl: CACHE_TTL.conditions, scope: 'station' },

    // Supplementary: Tide curve for chart (1-hour refresh matches Netlify Cache-Control)
    tideCurve: { key: 'curve', ttl: CACHE_TTL.tideCurve, scope: 'station' },

    // Legacy configs - kept for backwards compatibility, can be removed after migration verified
    tidePredictions: { key: 'predictions', ttl: CACHE_TTL.tidePredictions, scope: 'station' },
    weather: { key: 'weather', ttl: CACHE_TTL.weather, scope: 'station' },
    buoy: { key: 'buoy', ttl: CACHE_TTL.buoy, scope: 'buoy' },
    waterLevel: { key: 'waterlevel', ttl: CACHE_TTL.waterLevel, scope: 'station' },
    sunTimes: { key: 'sun-times', ttl: CACHE_TTL.sunTimes, scope: 'station' }
};

// Helper to build full cache key with region context
export function getCacheKey(configKey) {
    const config = CACHE_CONFIG[configKey];
    if (!config) return configKey;

    const regionId = AppState.currentRegion?.id || 'socal';

    if (config.scope === 'buoy') {
        const buoyId = getCurrentBuoy();
        return `tide-${regionId}-${buoyId}-${config.key}`;
    }

    const stationId = AppState.currentStationId || '9410170';
    return `tide-${regionId}-${stationId}-${config.key}`;
}
